/** 轻量富文本：纯文本 ↔ HTML，以及白名单消毒（含颜色） */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL',
  'BR', 'P', 'DIV', 'UL', 'OL', 'LI', 'SPAN', 'FONT',
]);

/** 明确丢弃的媒体标签（粘贴消毒时整节点删除） */
const STRIP_TAGS = new Set(['IMG', 'PICTURE', 'SOURCE', 'VIDEO', 'AUDIO', 'SVG', 'CANVAS', 'IFRAME', 'OBJECT', 'EMBED']);

export function isProbablyHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/** 展示/编辑前：纯文本转成可编辑 HTML；已是 HTML 则消毒 */
export function toEditorHtml(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  if (isProbablyHtml(raw)) return sanitizeRichText(raw);
  return escapeHtml(raw).replace(/\r\n|\r|\n/g, '<br>');
}

export function richTextToPlain(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  if (!isProbablyHtml(raw)) return raw;
  if (typeof document === 'undefined') {
    return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = sanitizeRichText(raw);
  return (div.textContent || div.innerText || '').replace(/\u00a0/g, ' ').trim();
}

export function isRichTextEmpty(value: string | null | undefined): boolean {
  return richTextToPlain(value).length === 0;
}

/** 失焦保存前统一成干净 HTML；空内容存空串 */
export function normalizeRichTextForSave(html: string | null | undefined): string {
  const sanitized = sanitizeRichText(html ?? '');
  if (isRichTextEmpty(sanitized)) return '';
  return sanitized;
}

function isSafeCssColor(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v || /url\s*\(|expression|javascript|@import/i.test(v)) return false;
  return /^(#[0-9a-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-z]{3,20})$/i.test(v);
}

/** 仅保留 color / background-color */
function sanitizeStyleAttr(style: string): string {
  const kept: string[] = [];
  for (const part of style.split(';')) {
    const idx = part.indexOf(':');
    if (idx < 0) continue;
    const key = part.slice(0, idx).trim().toLowerCase();
    const val = part.slice(idx + 1).trim();
    if ((key === 'color' || key === 'background-color') && isSafeCssColor(val)) {
      kept.push(`${key}: ${val}`);
    }
  }
  return kept.join('; ');
}

export function sanitizeRichText(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (STRIP_TAGS.has(el.tagName)) {
          el.remove();
          continue;
        }
        if (!ALLOWED_TAGS.has(el.tagName)) {
          const text = document.createTextNode(el.textContent || '');
          el.replaceWith(text);
          continue;
        }

        const styleKeep = el.getAttribute('style')
          ? sanitizeStyleAttr(el.getAttribute('style') || '')
          : '';
        const fontColor = el.tagName === 'FONT' ? (el.getAttribute('color') || '').trim() : '';

        for (const attr of Array.from(el.attributes)) {
          el.removeAttribute(attr.name);
        }
        if (styleKeep) el.setAttribute('style', styleKeep);
        if (fontColor && isSafeCssColor(fontColor)) el.setAttribute('color', fontColor);

        walk(el);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
      }
    }
  };
  walk(template.content);
  return template.innerHTML
    .replace(/(<br\s*\/?>\s*)+$/i, '')
    .replace(/^(<br\s*\/?>\s*)+/i, '')
    .trim();
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
